/**
 * Éditeur inline du `primary_broker_id` d'une entreprise.
 *
 * Refonte 9.P.2.R1 — affichage modernisé :
 *   - Mode lecture : avatar circle initials + nom + email + bouton « Modifier »
 *     inline (vs lien texte seul auparavant)
 *   - Mode édition : BrokerPicker + Save/Cancel inchangé fonctionnellement
 *
 * 8.C — limite shared connue (préservée stricte) :
 *   `UpdateCompanySchema` n'accepte pas `null` pour `primary_broker_id`
 *   (champ `.optional()` non `.nullable()`). On peut donc :
 *     - assigner pour la première fois (undefined → uuid)   ✅
 *     - réassigner (uuid A → uuid B)                          ✅
 *     - DÉTACHER (uuid → null) ❌ — caveat documenté.
 *
 * Mutation : PATCH /v1/companies/:id avec `{ primary_broker_id: <uuid> }`
 * via useUpdateCompany (8.A) qui invalide déjà ['companies'] + detail.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { BrokerPicker } from "../../../components/BrokerPicker";
import { ApiException } from "../../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../../lib/apiErrorMessage";
import { useBroker } from "../../../lib/queries/brokers";
import { useUpdateCompany } from "../../../lib/queries/companies";

type Props = {
  companyId: string;
  /** primary_broker_id actuel (peut être null = non assigné). */
  currentBrokerId: string | null;
};

/**
 * Génère 1-2 lettres en majuscule depuis un nom.
 * "Jean Tremblay" → "JT" ; "Marie" → "M" ; null/undefined/empty → "?".
 */
function getInitials(name: string | null | undefined): string {
  if (!name) return "?";
  const trimmed = name.trim();
  if (trimmed === "") return "?";
  const parts = trimmed.split(/\s+/);
  const first = parts[0]?.[0] ?? "";
  const second = parts[1]?.[0] ?? "";
  return (first + second).toUpperCase() || "?";
}

export function PrimaryBrokerEditor({ companyId, currentBrokerId }: Props) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string | undefined>(currentBrokerId ?? undefined);

  const update = useUpdateCompany(companyId);
  const currentBroker = useBroker(currentBrokerId ?? undefined);

  const onCancel = () => {
    setDraft(currentBrokerId ?? undefined);
    setEditing(false);
  };

  const onSave = async () => {
    if (!draft) return; // détachement non supporté en 8.C — bouton disabled
    if (draft === currentBrokerId) {
      setEditing(false);
      return;
    }
    try {
      await update.mutateAsync({ primary_broker_id: draft });
      setEditing(false);
    } catch {
      /* error visible via update.error */
    }
  };

  // ---- Mode lecture (refonte R1 — initials + nom + email) -------------------
  if (!editing) {
    const broker = currentBrokerId ? currentBroker.data : null;
    const isLoading = currentBrokerId && currentBroker.isLoading;
    const displayName = broker?.full_name ?? broker?.email ?? null;
    const displayEmail = broker?.email ?? null;
    const initials = getInitials(displayName);

    // Layout responsive (post-smoke R2) : flex-wrap + basis sur le bloc
    // avatar+label permet au bouton d'aller en row 2 quand la card est
    // étroite (side panel `lg:col-span-1` à viewport intermédiaire). Avant
    // ce fix, le label "Unassigned" sans truncate + button shrink-0 fixe
    // créaient un overlap visuel sur narrow widths.
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="flex min-w-0 flex-1 basis-[12rem] items-center gap-3">
          {currentBrokerId ? (
            <>
              <div
                aria-hidden="true"
                className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-brand-100 text-sm font-semibold text-brand-700"
              >
                {isLoading ? (
                  <span className="text-xs text-brand-400">…</span>
                ) : (
                  initials
                )}
              </div>
              <div className="min-w-0 flex-1">
                {isLoading ? (
                  <span className="text-sm text-slate-400">{t("app.loading")}</span>
                ) : (
                  <>
                    <Link
                      to={`/brokers/${currentBrokerId}`}
                      className="block truncate rounded-sm text-sm font-medium text-slate-900 hover:text-brand-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
                    >
                      {displayName ?? t("companies.primary_broker.unknown")}
                    </Link>
                    {displayEmail && displayEmail !== displayName && (
                      <p className="truncate text-xs text-slate-500">{displayEmail}</p>
                    )}
                  </>
                )}
              </div>
            </>
          ) : (
            <>
              <div
                aria-hidden="true"
                className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-dashed border-slate-300 bg-slate-50 text-slate-400"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm0 1.5c-2.5 0-5 1.25-5 3.5V14h10v-1c0-2.25-2.5-3.5-5-3.5Z"
                    stroke="currentColor"
                    strokeWidth="1.25"
                    fill="none"
                  />
                </svg>
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-slate-700">
                  {t("companies.primary_broker.unassigned")}
                </p>
              </div>
            </>
          )}
        </div>
        <button
          type="button"
          className="ml-auto shrink-0 rounded-md px-3 py-1.5 text-xs font-medium text-brand-700 hover:bg-brand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 touch-manipulation"
          onClick={() => setEditing(true)}
        >
          {currentBrokerId
            ? t("companies.primary_broker.change")
            : t("companies.primary_broker.assign")}
        </button>
      </div>
    );
  }

  // ---- Mode édition (inchangé fonctionnellement) ----------------------------
  return (
    <div className="space-y-3">
      <BrokerPicker
        id={`primary-broker-${companyId}`}
        value={draft}
        onChange={(id) => setDraft(id)}
        disabled={update.isPending}
      />

      {update.error && (
        <div
          role="alert"
          className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700"
        >
          {update.error instanceof ApiException
            ? getApiErrorMessage(update.error, lang) || t("errors.generic")
            : t("errors.generic")}
        </div>
      )}

      <p className="text-xs text-slate-500">
        {t("companies.primary_broker.detach_caveat")}
      </p>

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={update.isPending}
          className="rounded-md px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:opacity-50 touch-manipulation"
        >
          {t("common.cancel")}
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={
            update.isPending ||
            !draft || // détachement bloqué (cf. caveat shared)
            draft === currentBrokerId
          }
          className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300 touch-manipulation"
        >
          {update.isPending ? t("app.loading") : t("common.save")}
        </button>
      </div>
    </div>
  );
}
